// Shared page chrome for the public Trust Center pages.
// Explicitly light — these are public-facing documents that must be readable
// regardless of the app's dark-mode state.

import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail } from 'lucide-react';

export function TrustLayout({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    // Inline background/color forces light regardless of .dark class on <html>
    <div style={{ backgroundColor: '#f8fafc', color: '#1e293b', minHeight: '100vh' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link
            to="/trust"
            className="flex items-center gap-2 font-semibold"
            style={{ color: '#0f172a', textDecoration: 'none' }}
          >
            <div
              className="flex h-7 w-7 items-center justify-center"
              style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
            >
              <ShieldCheck style={{ width: 14, height: 14, color: '#16a34a' }} />
            </div>
            <span style={{ fontSize: 14 }}>Priorities.ai Trust Center</span>
          </Link>
          <a
            href="mailto:security@priorities.ai"
            className="flex items-center gap-1.5"
            style={{ fontSize: 13, color: '#64748b', textDecoration: 'none' }}
          >
            <Mail style={{ width: 13, height: 13 }} />
            security@priorities.ai
          </a>
        </div>
      </header>

      {/* ── Main content ────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-4xl px-6 py-12">
        <h1 className="sr-only">{title}</h1>
        <TrustProse>{children}</TrustProse>
      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid #e2e8f0', marginTop: 48 }}>
        <div
          className="mx-auto max-w-4xl px-6 py-6 flex items-center justify-between"
          style={{ fontSize: 12, color: '#94a3b8' }}
        >
          <span>Priorities.ai · Trust & Security</span>
          <span>security@priorities.ai · privacy@priorities.ai</span>
        </div>
      </footer>
    </div>
  );
}

// ── Inline prose renderer ─────────────────────────────────────────────────────
// Uses inline styles throughout so dark-mode Tailwind overrides can't bleed in.

function TrustProse({ children }: { children: React.ReactNode }) {
  return (
    <div className="trust-prose">
      <style>{`
        .trust-prose { color: #1e293b; }

        .trust-prose h1 {
          font-size: 2.25rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 0.5rem;
          line-height: 1.2;
          letter-spacing: -0.02em;
        }
        .trust-prose h2 {
          font-size: 1.25rem;
          font-weight: 650;
          color: #0f172a;
          margin: 2.5rem 0 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e2e8f0;
        }
        .trust-prose h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #0f172a;
          margin: 1.75rem 0 0.5rem;
        }
        .trust-prose h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #0f172a;
          margin: 1.5rem 0 0.4rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .trust-prose p {
          font-size: 0.9375rem;
          line-height: 1.75;
          color: #334155;
          margin: 0 0 1rem;
        }
        .trust-prose a {
          color: #1d4ed8;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .trust-prose a:hover { color: #1e40af; }

        .trust-prose ul, .trust-prose ol {
          margin: 0.75rem 0 1rem 1.25rem;
          padding: 0;
        }
        .trust-prose li {
          font-size: 0.9375rem;
          line-height: 1.7;
          color: #334155;
          margin-bottom: 0.25rem;
        }
        .trust-prose ul li { list-style-type: disc; }
        .trust-prose ol li { list-style-type: decimal; }

        /* Contents / document table */
        .trust-prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.25rem 0 2rem;
          font-size: 0.875rem;
          background: #ffffff;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .trust-prose thead tr {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        .trust-prose thead th {
          text-align: left;
          padding: 10px 14px;
          font-weight: 600;
          font-size: 0.8125rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .trust-prose tbody tr {
          border-bottom: 1px solid #f1f5f9;
        }
        .trust-prose tbody tr:last-child { border-bottom: none; }
        .trust-prose tbody tr:hover { background: #f8fafc; }
        .trust-prose td {
          padding: 11px 14px;
          color: #334155;
          vertical-align: top;
          line-height: 1.5;
        }
        .trust-prose td:first-child { color: #1e293b; }

        /* Blockquote callouts */
        .trust-prose blockquote {
          margin: 1.5rem 0;
          padding: 0.875rem 1.25rem;
          border-left: 3px solid #3b82f6;
          background: #eff6ff;
          border-radius: 0 8px 8px 0;
          color: #1e3a5f;
          font-size: 0.9375rem;
          line-height: 1.65;
        }
        .trust-prose blockquote p { color: inherit; margin: 0; }

        /* Inline code */
        .trust-prose code {
          background: #f1f5f9;
          color: #0f172a;
          padding: 1px 5px;
          border-radius: 4px;
          font-size: 0.85em;
          font-family: ui-monospace, monospace;
        }

        /* Horizontal rule */
        .trust-prose hr {
          border: none;
          border-top: 1px solid #e2e8f0;
          margin: 2.5rem 0;
        }

        /* Hero section — first h1 + following p */
        .trust-prose > h1:first-child {
          font-size: 2.5rem;
        }
      `}</style>
      {children}
    </div>
  );
}
